import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { Button } from '@/components/ui/Button';
import {
  GCAL_OAUTH_STATE_KEY,
  gcalRedirectUri,
} from '@/features/calendar/googleCalendar';

/**
 * Google Calendar OAuth2 redirect handler (per-user connection for the
 * Calendar page). Google redirects here with `code` + `state`. We:
 *   1. Verify `state` against the value stashed in sessionStorage by the
 *      Calendar page (CSRF protection).
 *   2. Forward `code`/`redirect_uri` to the google-calendar-oauth-callback
 *      Edge Function, which exchanges the code for a refresh token,
 *      persists it in vault, and records the connection.
 *   3. Bounce back to /calendar with a success toast.
 *
 * Same flow shape as Ga4CallbackPage, minus the property-picker step —
 * we always read the user's primary calendar.
 */
type Phase = { kind: 'exchanging' } | { kind: 'error'; message: string };

export function GoogleCalendarCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'exchanging' });
  // React 18 StrictMode mounts effects twice in dev; the OAuth `code` is
  // single-use, so guard against the second invocation eating the error.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error') ?? params.get('error_description');

    if (oauthError) {
      setPhase({ kind: 'error', message: `Google rejected the connection: ${oauthError}` });
      return;
    }
    if (!code) {
      setPhase({ kind: 'error', message: 'Missing OAuth code from Google.' });
      return;
    }

    const expectedState = sessionStorage.getItem(GCAL_OAUTH_STATE_KEY);
    if (!expectedState || !state || state !== expectedState) {
      setPhase({
        kind: 'error',
        message:
          'OAuth state mismatch. For your security, please retry the connection from the Calendar page.',
      });
      return;
    }
    sessionStorage.removeItem(GCAL_OAUTH_STATE_KEY);

    (async () => {
      const { data, error: fnError } = await supabase.functions.invoke<{
        ok: boolean;
        email: string | null;
        error?: string;
      }>('google-calendar-oauth-callback', {
        body: { code, redirect_uri: gcalRedirectUri() },
      });
      if (fnError) {
        const msg = await extractFunctionErrorMessage(fnError);
        setPhase({ kind: 'error', message: msg });
        return;
      }
      toast.success(
        data?.email
          ? `Google Calendar connected (${data.email}).`
          : 'Google Calendar connected.',
      );
      navigate('/calendar', { replace: true });
    })();
  }, [params, navigate]);

  if (phase.kind === 'error') {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="space-y-4 text-center">
          <h1 className="text-xl font-medium tracking-headline">Connection failed</h1>
          <p className="max-w-lg text-sm text-muted-foreground">{phase.message}</p>
          <Button variant="outline" onClick={() => navigate('/calendar', { replace: true })}>
            Back to Calendar
          </Button>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
